import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  type CommuteCalculatorResult,
  type PracticePrompt,
  type PracticeSessionSummary,
  type SalaryCalculatorResult,
} from '../../api/practiceHub';
import {
  useCommuteCalculator,
  usePracticeHub,
  usePracticeSession,
  useSalaryCalculator,
} from '../../hooks/usePracticeHub';
import { useCandidateProfileScore } from '../../hooks/useCandidateProfile';
import { useOccupationCatalog, useOccupationSearch } from '../../hooks/useOccupations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  BookOpen,
  Compass,
  MessageSquare,
  Rocket,
  Sparkles,
  Target,
  TrendingUp,
  Zap,
} from 'lucide-react';

type ChatMessage = {
  role: 'assistant' | 'user';
  text: string;
};

const formatSessionStatus = (session: PracticeSessionSummary) => {
  const date = new Date(session.lastActivity);
  return `${session.status === 'completed' ? 'Completed' : 'In progress'} · ${date.toLocaleDateString('en-GB', {
    month: 'short',
    day: 'numeric',
  })}`;
};

export function PracticeHubPage() {
  const { data: practiceHub, isPending, isError, error } = usePracticeHub();
  const { startSession, isStarting, sendMessage, isSending } = usePracticeSession();
  const commuteCalculator = useCommuteCalculator();
  const salaryCalculator = useSalaryCalculator();
  const { data: profileScore } = useCandidateProfileScore();

  const [selectedPrompt, setSelectedPrompt] = useState<PracticePrompt | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [roleOverride, setRoleOverride] = useState('');
  const [commuteForm, setCommuteForm] = useState({
    origin: '',
    destination: '',
    mode: 'public' as 'public' | 'drive' | 'cycle' | 'walk',
  });
  const [commuteResult, setCommuteResult] = useState<CommuteCalculatorResult | null>(null);
  const [salaryForm, setSalaryForm] = useState({
    role: '',
    location: '',
    experienceYears: 5,
  });
  const [salaryResult, setSalaryResult] = useState<SalaryCalculatorResult | null>(null);

  useEffect(() => {
    if (practiceHub?.calculators.commuteDefaults) {
      setCommuteForm((prev) => ({
        origin: practiceHub.calculators.commuteDefaults.origin,
        destination: practiceHub.calculators.commuteDefaults.destination,
        mode: practiceHub.calculators.commuteDefaults.mode ?? prev.mode,
      }));
    }
    if (practiceHub?.calculators.salaryDefaults) {
      setSalaryForm({
        role: practiceHub.calculators.salaryDefaults.role,
        location: practiceHub.calculators.salaryDefaults.location,
        experienceYears: practiceHub.calculators.salaryDefaults.experienceYears ?? 5,
      });
    }
  }, [practiceHub]);

  const careerPath = practiceHub?.careerPath;
  const occupationSearch = useOccupationSearch(roleOverride);
  const occupationCatalog = useOccupationCatalog(roleOverride);

  const effectiveRole = useMemo(() => {
    const override = roleOverride.trim();
    if (override.length > 1) return override;
    const focusRole = profileScore?.profile.focusRoles?.[0];
    if (focusRole && focusRole.trim().length > 1) return focusRole;
    if (careerPath?.targetRole) return careerPath.targetRole;
    return 'Candidate';
  }, [careerPath?.targetRole, profileScore?.profile.focusRoles, roleOverride]);

  const visiblePrompts = useMemo(() => {
    if (!practiceHub?.practicePrompts) return [];
    const roleText = effectiveRole.toLowerCase();
    const skillText = (profileScore?.profile.skills ?? []).join(' ').toLowerCase();
    const focusText = (profileScore?.profile.focusRoles ?? []).join(' ').toLowerCase();
    const textBlob = `${roleText} ${skillText} ${focusText}`;

    const scored = practiceHub.practicePrompts.map((prompt) => {
      const tags = prompt.tags ?? [];
      const hitCount = tags.reduce((score, tag) => {
        if (!tag) return score;
        return textBlob.includes(tag.toLowerCase()) ? score + 1 : score;
      }, 0);
      const titleHit = prompt.title.toLowerCase().split(' ').some((word) => textBlob.includes(word));
      const focusHit = prompt.focus.toLowerCase().split(' ').some((word) => textBlob.includes(word));
      const totalScore = hitCount + (titleHit ? 1 : 0) + (focusHit ? 1 : 0);
      return { prompt, score: totalScore };
    });

    const sorted = scored.sort((a, b) => b.score - a.score || a.prompt.difficulty.localeCompare(b.prompt.difficulty));
    const top = sorted.slice(0, 5).map((entry) => entry.prompt);
    // If everything scores zero, just take the first 5 as fallback.
    const hasSignal = sorted.some((entry) => entry.score > 0);
    if (!hasSignal) {
      return practiceHub.practicePrompts.slice(0, 5);
    }
    return top;
  }, [effectiveRole, practiceHub?.practicePrompts, profileScore?.profile.focusRoles, profileScore?.profile.skills]);

  const catalogRoleSuggestions = useMemo(() => {
    if (!occupationCatalog.data?.roles) return [];
    const trimmed = roleOverride.trim().toLowerCase();
    const roles = trimmed.length
      ? occupationCatalog.data.roles.filter((role) => {
          const haystack = `${role.title} ${role.categoryLabel} ${(role.aliases ?? []).join(' ')}`.toLowerCase();
          return haystack.includes(trimmed);
        })
      : occupationCatalog.data.roles;
    return roles.slice(0, 10);
  }, [occupationCatalog.data?.roles, roleOverride]);

  const handleStartSession = async (prompt: PracticePrompt) => {
    if (!practiceHub) return;
    setSelectedPrompt(prompt);
    try {
      const response = await startSession({
        role: effectiveRole,
        focusArea: prompt.focus,
        difficulty: prompt.difficulty,
        occupationId: occupationSearch.data?.[0]?.id,
      });
      setSessionId(response.sessionId);
      setChatMessages([
        { role: 'assistant', text: response.openingQuestion } as ChatMessage,
        ...response.guidance.map((tip) => ({ role: 'assistant' as const, text: tip })),
      ]);
      toast.success('Session ready �?');
    } catch (err) {
      console.error(err);
      toast.error('Unable to start a session right now.');
    }
  };

  const handleSendMessage = async () => {
    if (!sessionId) {
      toast.message('Start a session first.');
      return;
    }
    const trimmed = chatInput.trim();
    if (!trimmed) return;
    setChatMessages((prev) => [...prev, { role: 'user' as const, text: trimmed }]);
    setChatInput('');
    try {
      const response = await sendMessage({ sessionId, message: trimmed });
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', text: response.reply } as ChatMessage,
        ...(response.followUpQuestion ? [{ role: 'assistant' as const, text: response.followUpQuestion }] : []),
      ]);
      if (response.done) {
        setSessionId(null);
      }
    } catch (err) {
      console.error(err);
      toast.error('Practice coach is unavailable.');
    }
  };

  const handleCommuteSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const result = await commuteCalculator.mutateAsync(commuteForm);
      setCommuteResult(result);
    } catch (err) {
      console.error(err);
      toast.error('Unable to calculate commute right now.');
    }
  };

  const handleSalarySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const result = await salaryCalculator.mutateAsync(salaryForm);
      setSalaryResult(result);
    } catch (err) {
      console.error(err);
      toast.error('Unable to refresh salary insight.');
    }
  };

  const heroStats = useMemo(() => {
    if (!practiceHub) return [];
    return [
      { label: 'Practice prompts', value: practiceHub.practicePrompts.length },
      { label: 'Coach tips', value: practiceHub.aiCoachTips.length },
      { label: 'Recent sessions', value: practiceHub.recentSessions.length },
    ];
  }, [practiceHub]);

  return (
    <main
      className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(186,144,54,0.15),transparent_50%)] px-4 py-10 sm:px-6 lg:px-12"
      aria-labelledby="practice-hub-title"
    >
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#ba9036]">Candidate Practice Hub</p>
              <h1 id="practice-hub-title" className="text-3xl font-semibold text-white">Interview prep, career path, and calculators</h1>
              <p className="mt-3 text-gray-300">
                Run AI-guided practice sessions, understand your career trajectory, and plan commute & salary expectations.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 rounded-xl border border-white/10 bg-black/30 p-4 text-center text-sm text-gray-300">
              {heroStats.map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl font-semibold text-white">{stat.value}</p>
                  <p>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </header>

        {isError && (
          <Card className="border-red-400/30 bg-red-500/10">
            <CardContent className="p-4 text-sm text-red-200">
              {error instanceof Error ? error.message : 'Unable to load practice hub data.'}
            </CardContent>
          </Card>
        )}

        <section className="grid gap-6 lg:grid-cols-3" aria-label="Practice prompts and session">
          <Card className="border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Sparkles className="h-5 w-5 text-[#ba9036]" />
                Practice prompts
              </CardTitle>
              <CardDescription>Select a prompt to start a coaching session.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(isPending || !practiceHub) && (
                <p className="text-sm text-gray-400">Loading prompts…</p>
              )}
              {visiblePrompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className={`rounded-lg border p-4 ${
                    selectedPrompt?.id === prompt.id ? 'border-[#d2ac47]/70 bg-white/10' : 'border-white/10 bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-white">{prompt.title}</p>
                      <p className="text-xs text-gray-400">{prompt.description}</p>
                    </div>
                    <Badge variant="outline" className="border-white/20 text-xs capitalize">
                      {prompt.difficulty}
                    </Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-400">
                    <span>{prompt.durationMinutes} min</span>
                    <span className="rounded-full bg-black/30 px-2 py-0.5 text-[#ba9036]">{prompt.focus}</span>
                  </div>
                  <Button
                    onClick={() => handleStartSession(prompt)}
                    disabled={isStarting}
                    className="mt-3 w-full bg-gold-gradient text-black hover:brightness-110"
                  >
                    {isStarting && selectedPrompt?.id === prompt.id ? 'Preparing…' : 'Start session'}
                  </Button>
                </div>
              ))}
              <div>
                <h4 className="text-sm font-semibold text-white">Recent sessions</h4>
                <div className="mt-3 space-y-3 text-sm text-gray-300">
                  {practiceHub?.recentSessions.map((session) => (
                    <div key={session.id} className="rounded-lg border border-white/10 bg-white/5 p-3">
                      <p className="text-white">{session.role}</p>
                      <p className="text-xs text-gray-400">{formatSessionStatus(session)}</p>
                      <p className="mt-1 text-xs text-gray-400">Focus: {session.focus}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <MessageSquare className="h-5 w-5 text-[#ba9036]" />
                Practice session
              </CardTitle>
              <CardDescription>
                Your AI interview coach analyses your answers and suggests improvements.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-black/40 p-4 text-sm text-gray-200 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wide text-gray-400">Practice for role</p>
                  <Input
                    value={roleOverride}
                    onChange={(event) => setRoleOverride(event.target.value)}
                    placeholder={effectiveRole}
                    className="mt-1 bg-white/5 text-white placeholder:text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Defaults to your profile focus role; override to rehearse for a different job.
                  </p>
                  {catalogRoleSuggestions.length > 0 && (
                    <div className="mt-2 space-y-2 rounded-md border border-white/10 bg-black/50 p-2">
                      <p className="text-[11px] uppercase tracking-wide text-gray-400">Common UK roles</p>
                      <div className="flex flex-wrap gap-2">
                        {catalogRoleSuggestions.map((role) => (
                          <button
                            key={role.id}
                            type="button"
                            onClick={() => setRoleOverride(role.title)}
                            className="rounded bg-white/5 px-2 py-1 text-left text-sm text-gray-100 hover:bg-white/10"
                          >
                            <span className="font-medium text-white">{role.title}</span>
                            <span className="block text-[10px] text-gray-400">{role.categoryLabel}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {occupationSearch.data && occupationSearch.data.length > 0 && (
                    <div className="mt-2 space-y-1 rounded-md border border-white/10 bg-black/50 p-2">
                      <p className="text-[11px] uppercase tracking-wide text-gray-400">Suggestions (ESCO)</p>
                      <div className="space-y-1">
                        {occupationSearch.data.slice(0, 5).map((occ) => (
                          <button
                            key={occ.id}
                            type="button"
                            onClick={() => {
                              setRoleOverride(occ.preferredLabel);
                            }}
                            className="w-full rounded bg-white/5 px-2 py-1 text-left text-sm text-gray-100 hover:bg-white/10"
                          >
                            <span className="font-medium text-white">{occ.preferredLabel}</span>
                            {occ.altLabels?.length ? (
                              <span className="block text-[11px] text-gray-400">
                                Also known as: {occ.altLabels.slice(0, 2).join(', ')}
                              </span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      const response = await startSession({
                        role: effectiveRole,
                        focusArea: 'general interview',
                        difficulty: 'medium',
                        occupationId: occupationSearch.data?.[0]?.id,
                      });
                      setSessionId(response.sessionId);
                      setChatMessages([
                        { role: 'assistant', text: response.openingQuestion } as ChatMessage,
                        ...response.guidance.map((tip) => ({ role: 'assistant' as const, text: tip })),
                      ]);
                      toast.success('Session ready to start.');
                    } catch (err) {
                      console.error(err);
                      toast.error('Unable to start a session right now.');
                    }
                  }}
                  disabled={isStarting}
                  className="w-full bg-gold-gradient text-black hover:brightness-110 sm:w-auto"
                >
                  {isStarting ? 'Preparing…' : 'Start session'}
                </Button>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/30">
                <ScrollArea className="h-80 p-4">
                  {chatMessages.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center text-center text-gray-400">
                      <MessageSquare className="mb-4 h-10 w-10 opacity-50" />
                      <p>Choose a prompt to begin your session.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {chatMessages.map((msg, idx) => (
                        <div key={`${msg.role}-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[80%] rounded-xl px-4 py-3 text-sm ${
                              msg.role === 'user' ? 'bg-gold-gradient text-black' : 'bg-white/10 text-gray-100'
                            }`}
                          >
                            {msg.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                <div className="border-t border-white/10 p-4">
                  <div className="flex gap-3">
                    <Textarea
                      placeholder="Share your answer or ask a question…"
                      value={chatInput}
                      onChange={(event) => setChatInput(event.target.value)}
                      disabled={!sessionId || isSending}
                      className="min-h-[80px] flex-1 resize-none bg-white/5 text-white placeholder:text-gray-400"
                    />
                    <div className="flex flex-col gap-3">
                      <Button
                        onClick={handleSendMessage}
                        disabled={!sessionId || isSending}
                        className="bg-gold-gradient text-black hover:brightness-110"
                      >
                        {isSending ? 'Sending…' : 'Send'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSessionId(null);
                          setChatMessages([]);
                        }}
                        className="border-white/20 text-white hover:bg-white/5"
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              {practiceHub?.aiCoachTips && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                    <Zap className="h-4 w-4 text-[#ba9036]" />
                    AI coach tips
                  </p>
                  <ul className="space-y-2 text-sm text-gray-300">
                    {practiceHub.aiCoachTips.map((tip, idx) => (
                      <li key={idx} className="flex gap-2">
                        <span className="text-[#ba9036]">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <Card className="border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Compass className="h-5 w-5 text-[#ba9036]" />
                Career path
              </CardTitle>
              <CardDescription>See the milestones between you and your next role.</CardDescription>
            </CardHeader>
            <CardContent>
              {!careerPath ? (
                <p className="text-sm text-gray-400">Loading career path…</p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-300">
                      {careerPath.currentRole} ➜ {careerPath.targetRole}
                    </p>
                    <p className="text-gray-400">{careerPath.summary}</p>
                  </div>
                  <div className="space-y-3">
                    {careerPath.steps.map((step) => (
                      <div key={step.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-white">{step.label}</p>
                          <Badge
                            className={
                              step.status === 'completed'
                                ? 'bg-green-500/20 text-green-300'
                                : step.status === 'in-progress'
                                ? 'bg-[#ba9036]/20 text-[#ba9036]'
                                : 'bg-white/10 text-gray-300'
                            }
                          >
                            {step.status.replace('-', ' ')}
                          </Badge>
                        </div>
                        <p className="mt-2 text-sm text-gray-300">{step.description}</p>
                        <p className="text-xs text-gray-500">{step.timeline}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Target className="h-5 w-5 text-[#ba9036]" />
                Recommendations
              </CardTitle>
              <CardDescription>High-leverage moves suggested by Lighthouse coaches.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {!careerPath ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : (
                careerPath.recommendations.map((rec) => (
                  <div key={rec.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <p className="text-white">{rec.title}</p>
                    <p className="text-sm text-gray-300">{rec.description}</p>
                    <Button asChild variant="outline" className="mt-3 border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10">
                      <a href={rec.actionLink}>{rec.actionLabel}</a>
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <Card className="border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Rocket className="h-5 w-5 text-[#ba9036]" />
                Commute planner
              </CardTitle>
              <CardDescription>Estimate commute time, cost, and carbon.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCommuteSubmit} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-400">Home postcode</label>
                  <Input
                    value={commuteForm.origin}
                    onChange={(event) => setCommuteForm((prev) => ({ ...prev, origin: event.target.value }))}
                    className="mt-1 bg-white/5 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-400">Work postcode</label>
                  <Input
                    value={commuteForm.destination}
                    onChange={(event) => setCommuteForm((prev) => ({ ...prev, destination: event.target.value }))}
                    className="mt-1 bg-white/5 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-400">Mode</label>
                  <Select
                    value={commuteForm.mode}
                    onValueChange={(mode: 'public' | 'drive' | 'cycle' | 'walk') =>
                      setCommuteForm((prev) => ({ ...prev, mode }))
                    }
                  >
                    <SelectTrigger className="mt-1 bg-white/5 text-white">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public transport</SelectItem>
                      <SelectItem value="drive">Drive</SelectItem>
                      <SelectItem value="cycle">Cycle</SelectItem>
                      <SelectItem value="walk">Walk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={commuteCalculator.isPending} className="w-full bg-gold-gradient text-black">
                  {commuteCalculator.isPending ? 'Calculating…' : 'Calculate commute'}
                </Button>
              </form>
              {commuteResult && (
                <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-4 text-sm text-gray-200">
                  <p className="text-lg font-semibold text-white">{commuteResult.durationMinutes} min door-to-door</p>
                  <p className="text-gray-400">
                    Weekly £{commuteResult.weeklyCost.toFixed(0)} · Annual £{commuteResult.annualCost.toLocaleString()}
                  </p>
                  <p className="text-gray-400">Carbon impact: {commuteResult.carbonKg}kg CO₂ / week</p>
                  <ul className="mt-3 space-y-1 text-xs text-gray-400">
                    {commuteResult.insights.map((insight, idx) => (
                      <li key={idx}>• {insight}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <TrendingUp className="h-5 w-5 text-[#ba9036]" />
                Salary insight
              </CardTitle>
              <CardDescription>Understand the market range for your role and location.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSalarySubmit} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-400">Role</label>
                  <Input
                    value={salaryForm.role}
                    onChange={(event) => setSalaryForm((prev) => ({ ...prev, role: event.target.value }))}
                    className="mt-1 bg-white/5 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-400">Location</label>
                  <Input
                    value={salaryForm.location}
                    onChange={(event) => setSalaryForm((prev) => ({ ...prev, location: event.target.value }))}
                    className="mt-1 bg-white/5 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-400">Experience (years)</label>
                  <Input
                    type="number"
                    min={0}
                    max={40}
                    value={salaryForm.experienceYears}
                    onChange={(event) =>
                      setSalaryForm((prev) => ({ ...prev, experienceYears: Number(event.target.value) }))
                    }
                    className="mt-1 bg-white/5 text-white"
                  />
                </div>
                <Button type="submit" disabled={salaryCalculator.isPending} className="w-full bg-gold-gradient text-black">
                  {salaryCalculator.isPending ? 'Refreshing…' : 'Refresh salary insight'}
                </Button>
              </form>
              {salaryResult && (
                <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-4 text-sm text-gray-200">
                  <p className="text-lg font-semibold text-white">
                    £{salaryResult.marketRange.median.toLocaleString()} median · {salaryResult.percentile}th percentile
                  </p>
                  <p className="text-gray-400">
                    Range £{salaryResult.marketRange.min.toLocaleString()} - £{salaryResult.marketRange.max.toLocaleString()}
                  </p>
                  <ul className="mt-3 space-y-1 text-xs text-gray-400">
                    {salaryResult.insights.map((insight, idx) => (
                      <li key={idx}>• {insight}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </main>
  );
}
